import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { removeLocalStorageValue } from '../../hooks/useLocalStorageValue/utils';
import { asyncNoop } from '../../utils/lodash';
import {
  PreferredCurrency,
  PreferredPayment,
} from '../../setup/apiTypes/enums';
import SupportedLanguages from '../../setup/intl/enums';
import { AUTH_TOKENS_LOCAL_STORAGE_KEY } from './consts';
import { UserRole } from './enums';
import { useAuthTokens } from './hooks';
import { User, UserContextProviderProps } from './types';
import { fetchUser } from './utils';

// ── DEV/TEST AUTH BYPASS (env-gated, OFF by default) ──
// When REACT_APP_DEV_AUTH === '1', the User context short-circuits to a mock
// buyer instead of calling the backend /me — letting the team render and walk
// the storefront buy→PO flow without real SSO (the Auth0 wall otherwise
// redirects /marketplace to /sign-in). CRA inlines the literal at build time,
// so this whole branch is dead-code-eliminated when the flag is unset — it can
// never run in a normal/prod build. NOT a production code path.
const DEV_AUTH = process.env.REACT_APP_DEV_AUTH === '1';
// Settable buyer id so the team can walk as different cohort buyers in the
// persona test; this id flows into buyer headers + funnel events.
const DEV_AUTH_BUYER_ID =
  process.env.REACT_APP_DEV_AUTH_BUYER_ID || 'dev-preview';
// Display name for the dev user — set per merchant on test-day sessions.
const DEV_AUTH_NAME = process.env.REACT_APP_DEV_AUTH_NAME || 'Dev Buyer';

const makeDevUser = (): User => ({
  id: DEV_AUTH_BUYER_ID,
  permissions: [],
  roles: [UserRole.Admin],
  createdAt: new Date().toISOString(),
  name: DEV_AUTH_NAME,
  phoneNumber: '+9647700000000',
  email: 'dev-buyer@sandoog.test',
  picture: null,
  accountDetails: {
    emailAddress: 'dev-buyer@sandoog.test',
    phoneNumber: '+9647700000000',
    companyName: 'Dev Merchant LLC',
    companyAddress: 'Mansour, Baghdad',
    logo: null,
    language: process.env.REACT_APP_DEV_AUTH_LANG === 'ar' ? SupportedLanguages.Arabic : SupportedLanguages.English,
  },
  billingAndPayment: {
    preferredCurrency: PreferredCurrency.IQD,
    preferredPayment: PreferredPayment.BankTransfer,
  },
  isSandoogUser: false,
  isAdvancedUser: true,
  isAllowedPrinter: false,
  clientId: 'dev-client-1',
});

export interface UserContextValue {
  user: User | undefined;
  isUserLoading: boolean;
  refetchUserData: () => Promise<void>;
}

export const UserContext = React.createContext<UserContextValue>({
  user: undefined,
  isUserLoading: false,
  refetchUserData: asyncNoop,
});

const { Provider } = UserContext;

export const UserContextProvider: React.FC<UserContextProviderProps> = ({
  children,
}) => {
  const authTokens = useAuthTokens();
  const [user, setUser] = useState<User>();
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [isUserInitialLoading, setIsUserInitialLoading] = useState(true);
  const fetchUserData = useCallback(async () => {
    setIsUserLoading(true);

    // DEV/TEST bypass: skip the backend /me call and use a mock buyer so the
    // app renders without real SSO. Dead-code-eliminated when the flag is off.
    if (DEV_AUTH) {
      setUser(makeDevUser());
      setIsUserInitialLoading(false);
      setIsUserLoading(false);
      return;
    }

    try {
      if (authTokens) {
        const userData = await fetchUser(authTokens);

        setUser(userData);
      } else {
        setUser(undefined);
      }
    } catch (_) {
      removeLocalStorageValue(AUTH_TOKENS_LOCAL_STORAGE_KEY);
    }

    setIsUserInitialLoading(false);
    setIsUserLoading(false);
  }, [authTokens]);

  const userContextValue = useMemo(
    () => ({
      user,
      isUserLoading,
      refetchUserData: fetchUserData,
    }),
    [user, isUserLoading, fetchUserData]
  );

  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTokens]);

  return (
    <Provider value={userContextValue}>
      {children(isUserInitialLoading)}
    </Provider>
  );
};
